// @flow

import {useCallback, useEffect, useState} from 'react';
import {fetchIncomingPayment, type IncomingPayment} from '@tectonics/prs/incoming-payment';
import {useControllerContext, useLocalTenancyAsTenant} from './context';
import {useLanguage} from '@tectonics/controller/language/LanguageContext';
import {getDefaultCountry} from '@tectonics/controller/language/language-service';
import initI18n, {initI18nWithoutPayment} from '@tectonics/i18n';
import {handleError} from '@tectonics/observability';

async function reportingFailure(init: () => Promise<void>): Promise<void> {
  try {
    await init();
  } catch (e) {
    handleError(e);
  }
}

function initTranslations(country: string): Promise<void> {
  return reportingFailure(() => initI18n(country));
}

export function useFetchIncomingPayment(setIncomingPayment: (IncomingPayment) => void): [bool, ?Error, () => void] {
  const [fetching, setFetching] = useState<boolean>(true);
  const [fetchingError, setFetchingError] = useState<?Error>(null);
  const {hash} = useControllerContext();
  const tenant = useLocalTenancyAsTenant();
  const [reloadCounter, setReloadCounter] = useState<number>(0);
  const requestReload = useCallback(() => {
    setReloadCounter(reloadCounter => reloadCounter + 1);
  }, []);
  const {addCountry} = useLanguage();

  useEffect(() => {
    setFetching(true);
    setFetchingError(null);

    (async () => {
      try {
        const incomingPayment = await fetchIncomingPayment(hash, tenant);
        const {country} = incomingPayment;
        await addCountry(country);
        await initTranslations(country);
        setIncomingPayment(incomingPayment);
        setFetching(false);
      } catch (e) {
        const defaultCountry = getDefaultCountry();
        await initI18nWithoutPayment(defaultCountry);
        setFetchingError(e);
        setFetching(false);
      }
    })();
  }, [hash, setIncomingPayment, setFetching, tenant, reloadCounter]);

  return [fetching, fetchingError, requestReload];
}
